'use client';

import { useState } from 'react';

import { useTranslation } from '@/lib/i18n/i18n.client';

import { SvgIcon } from '@/components/SvgIcon';

import { INFO_MAX_LENGTH } from '@/blocks/JobApplicationForm/constants/form';
import Button from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import RichTextFieldStrapi from '@/components/atoms/RichTextField/RichTextFieldStrapi';

import { CaptureLeadPopupProps } from './LeadPopUpModal';
import useLeadPopUpForm, { MULTIPLE_INPUTS } from './useLeadPopUpForm';
import ImageStrapi from '../../atoms/Image/ImageStrapi';
import { InputDropdown } from '../../atoms/InputDropdown';

import iconSpinnerSrc from '~/svg/Spinner.svg';

export const LeadPopUp = (
  props: CaptureLeadPopupProps & {
    onSubmitResult?: (status: 'success' | 'invalid' | 'error') => void;
  }
) => {
  const { texts } = useTranslation();
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const [selectedDropdownValue, setSelectedDropdownValue] = useState('');

  const {
    errors,
    handleSubmit,
    values,
    getFieldProps,
    setFieldValue,
    isLoading,
  } = useLeadPopUpForm(props);

  const handleDropdownChange = (value: string) => {
    setSelectedDropdownValue(value);
    setFieldValue(MULTIPLE_INPUTS.accelerator, value);
  };
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setWasSubmitted(true);
    const result = await handleSubmit();
    if (!props.onSubmitResult) return;

    props.onSubmitResult(result);
    if (result === 'success') {
      setWasSubmitted(false);
    }
  };

  const areRequiredFieldsFilled = (() => {
    if (props.multipleInputs) {
      for (const input of props.multipleInputs) {
        if (!input.isRequired) continue;
        if (input.fieldMapping === MULTIPLE_INPUTS.accelerator) {
          if (!selectedDropdownValue) return false;
        } else {
          const fieldValue = values[input.fieldMapping as keyof typeof values];
          if (!fieldValue) return false;
        }
      }
    }

    if (props.dropdownOptions?.dropdownOptions && !selectedDropdownValue) {
      return false;
    }

    return true;
  })();

  const shouldDisplayImage =
    props.image && (!props.multipleInputs || props.multipleInputs.length <= 1);

  const dropDownInput = () => {
    return (
      <>
        {props.dropdownOptions?.dropdownOptions && (
          <InputDropdown
            name='dropdown'
            onOptionChange={handleDropdownChange}
            classNames={{
              inputField: {
                root: 'dark:bg-gray-50 bg-base-white',
              },
              dropdown:
                'dark:bg-gray-50 bg-base-white text-gray-950 dark:text-base-dark-white font-mono',
            }}
            label={
              props.dropdownOptions?.dropDownLabel
                ? `${props.dropdownOptions.dropDownLabel} *`
                : props.dropdownOptions?.dropDownLabel
            }
            options={props.dropdownOptions?.dropdownOptions}
            value={selectedDropdownValue}
          />
        )}
      </>
    );
  };

  return (
    <div className='flex md:gap-x-8 md:min-w-[400px] w-full dark:bg-gray-50 bg-base-white items-center'>
      {shouldDisplayImage && (
        <ImageStrapi
          block={props.image}
          inner={{ classNames: { image: 'hidden md:block ' } }}
          height={272}
          width={242}
        />
      )}
      <div className='flex flex-col w-full'>
        <div className='text-h3 mb-4 font-serif md:flex w-full mr-10'>
          <RichTextFieldStrapi block={props.title} />
        </div>
        <div className='text-body-l font-mono text-gray-600 mb-4 md:flex w-full'>
          <RichTextFieldStrapi block={props.description} />
        </div>
        <form
          className='flex flex-col text-left w-full'
          onSubmit={handleFormSubmit}
          data-test-id='gated-content-form'
        >
          {props.multipleInputs?.map((input) => {
            switch (input.fieldMapping) {
              case MULTIPLE_INPUTS.useCase:
              case MULTIPLE_INPUTS.websiteUrl:
              case MULTIPLE_INPUTS.companyName:
              case MULTIPLE_INPUTS.lastName:
              case MULTIPLE_INPUTS.firstName:
                return (
                  <Input
                    placeholder={texts.gated_content.placeholder_email}
                    label={
                      input.isRequired
                        ? `${input.fieldLabel} *`
                        : input.fieldLabel
                    }
                    required={input.isRequired}
                    error={
                      wasSubmitted &&
                      (!values[input.fieldMapping] ||
                        errors[input.fieldMapping])
                        ? texts.submission_status_message.error
                        : errors[input.fieldMapping] || ''
                    }
                    {...getFieldProps(input.fieldMapping)}
                    classNames={{
                      input: {
                        inputElement: 'dark:bg-gray-50 bg-base-white',
                      },
                      label: {
                        root: 'text-gray-950 font-mono',
                        error: 'text-gray-950',
                      },
                      bottomText: { root: 'h-6' },
                    }}
                  />
                );
              case MULTIPLE_INPUTS.email:
              case MULTIPLE_INPUTS.companyEmail:
                return (
                  <Input
                    required={input.isRequired}
                    {...getFieldProps(input.fieldMapping)}
                    placeholder={texts.gated_content.placeholder_email}
                    label={
                      input.isRequired
                        ? `${input.fieldLabel} *`
                        : input.fieldLabel
                    }
                    error={
                      wasSubmitted &&
                      (!values[input.fieldMapping] ||
                        errors[input.fieldMapping])
                        ? texts.submission_status_message.invalid_email
                        : errors[input.fieldMapping] || ''
                    }
                    classNames={{
                      input: {
                        inputElement: 'dark:bg-gray-50 bg-base-white',
                      },
                      label: {
                        root: 'text-gray-950 font-mono',
                        error: 'text-gray-950',
                      },
                      bottomText: { root: 'h-6' },
                    }}
                  />
                );
              case MULTIPLE_INPUTS.message:
                return (
                  <Input
                    as='textarea'
                    required={input.isRequired}
                    label={
                      input.isRequired
                        ? `${input.fieldLabel} *`
                        : input.fieldLabel
                    }
                    {...getFieldProps(input.fieldMapping)}
                    maxLength={INFO_MAX_LENGTH}
                    classNames={{
                      textarea: {
                        root: 'dark:bg-gray-50 bg-base-white',
                      },
                      input: {
                        inputElement: 'dark:bg-gray-50 bg-base-white',
                      },
                    }}
                  />
                );
              case MULTIPLE_INPUTS.accelerator:
                return dropDownInput();
              default:
                return null;
            }
          })}
          <Button
            classNames={{
              root: 'w-full mb-6',
            }}
            onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) =>
              e.preventDefault()
            }
            type='submit'
            disabled={!areRequiredFieldsFilled || isLoading}
          >
            {isLoading ? (
              <span className='flex items-center gap-2'>
                <SvgIcon
                  src={iconSpinnerSrc}
                  asImg
                  className='animate-[spin_2s_linear_infinite]'
                />
                {texts.lead_popup.form.submitting}
              </span>
            ) : (
              <RichTextFieldStrapi block={props.submitButtonText} />
            )}
          </Button>
        </form>
        <div className='text-gray-600 text-body-m font-mono w-full'>
          <RichTextFieldStrapi block={props.termsAndConditions} />
        </div>
        <div />
      </div>
    </div>
  );
};
